import { useEffect, useRef, useState } from "react";
import { type DeviceStatus, fetchDeviceStatus } from "../api";
import { formatClock, formatDateTimeShort } from "../format";

/**
 * "Apparaatstatus" home card: the async confirmation that the install
 * came online, with "enrolled" kept apart from "healthy" (online /
 * pending / offline). The headline reads "Apparaat" only when a real
 * edge device heartbeats, "Installatie" for cloud-connected sites.
 * Polls at 30 s and keeps the last good value on a fetch error (a
 * blip must not read as "device down").
 * docs/customer-app.md > Front-end > Device status card
 */

const POLL_MS = 30_000;

export function DeviceStatusCard() {
  const [status, setStatus] = useState<DeviceStatus | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      const my = ++seq.current;
      try {
        const v = await fetchDeviceStatus();
        if (!cancelled && my === seq.current) setStatus(v);
      } catch {
        /* keep stale value; don't flash a false "offline" on a blip */
      }
    }
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Skeleton until the first response lands.
  if (status == null) {
    return (
      <section className="card p-4 flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full bg-line shrink-0" />
        <div className="h-4 w-40 bg-line/40 rounded animate-pulse" />
      </section>
    );
  }

  const v = VIEW[status.state] ?? VIEW.offline;
  const subject = status.edge_device ? "Apparaat" : "Installatie";
  const detail = describe(status);

  return (
    <section
      className={`rounded-2xl shadow-card p-4 flex items-center gap-3 border ${v.box}`}
      role="status"
      aria-live="polite"
    >
      <span className={`relative flex w-2.5 h-2.5 shrink-0`}>
        {status.state === "online" && (
          <span className="absolute inline-flex w-full h-full rounded-full bg-accent opacity-60 motion-safe:animate-ping" />
        )}
        <span className={`relative inline-flex rounded-full w-2.5 h-2.5 ${v.dot}`} />
      </span>
      <div className="min-w-0">
        <div className={`text-sm font-medium ${v.title}`}>{v.label(subject)}</div>
        {detail && <div className="text-xs text-ink-mute mt-0.5">{detail}</div>}
      </div>
    </section>
  );
}

const VIEW: Record<
  DeviceStatus["state"],
  { label: (subject: string) => string; box: string; dot: string; title: string }
> = {
  online: {
    label: (s) => `${s} online`,
    box: "bg-accent-soft/40 border-accent/30",
    dot: "bg-accent",
    title: "text-ink",
  },
  pending: {
    label: (s) => `${s} nog niet helemaal online`,
    box: "bg-sun-soft/40 border-sun/30",
    dot: "bg-sun",
    title: "text-ink",
  },
  offline: {
    label: () => "Geen verbinding",
    box: "bg-danger-soft/40 border-danger/30",
    dot: "bg-danger",
    title: "text-ink",
  },
};

/** "14:32" today, "3 sep 14:32" on an earlier day - a bare clock would
 *  be ambiguous after 24h, and uptimes run to weeks. */
function when(iso: string): string {
  return new Date(iso).toDateString() === new Date().toDateString()
    ? formatClock(iso)
    : formatDateTimeShort(iso);
}

/** Second line under the headline - the specific human-readable detail
 *  per state. Kept out of VIEW because it depends on the live `since` /
 *  `battery_present` values, not just the state enum. */
function describe(status: DeviceStatus): string {
  switch (status.state) {
    case "online":
      // Online but no battery reporting: still the soft hint - being
      // online doesn't mean fully healthy.
      if (!status.battery_present) {
        return "Batterij nog niet gevonden - controleer of de accu aanstaat.";
      }
      if (!status.since) return "Verbonden";
      switch (status.since_kind) {
        case "uptime":
          return `Aan sinds ${when(status.since)}`;
        case "streak":
          return status.since_capped
            ? "Gegevens komen al meer dan 30 dagen ononderbroken binnen"
            : `Gegevens komen ononderbroken binnen sinds ${when(status.since)}`;
        default:
          return "Verbonden";
      }
    case "pending":
      if (status.battery_present && status.since) {
        // Data exists but is slow: say when we last heard something,
        // not "wordt opgestart" on an installation that runs for months.
        return `Verbinding hapert - laatste gegevens ${when(status.since)}.`;
      }
      return "Nog geen batterijgegevens. Dit kan een paar minuten duren.";
    case "offline":
      if (status.since) {
        return `Laatst gezien ${formatDateTimeShort(status.since)}. Stefan is gewaarschuwd.`;
      }
      return "Nog niet aangemeld. Volg de stappen in de doos om je apparaat op te starten.";
    default:
      return "";
  }
}
